import { Link, useLocation } from "react-router";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Seo } from "@/components/seo";
import { Button } from "@/components/ui/button";
import { ProductDemo } from "@/components/marketing/product-demo";
import { FinalCta } from "./home";
import { SEO_PAGES } from "@/routes/seo";

export interface LandingContent {
  eyebrow: string;
  title: string;
  subtitle: string;
  blocks: { title: string; text: string }[];
  checklist: string[];
  quote?: { text: string; who: string };
}

/** Feature & audience pages share one layout; content lives in the configs below. */
export function LandingPage({ c }: { c: LandingContent }) {
  const { pathname } = useLocation();
  const seo = SEO_PAGES[pathname];
  return (
    <>
      {seo && <Seo title={seo.title} description={seo.description} path={pathname} />}
      <section className="mx-auto grid max-w-6xl items-center gap-12 px-5 pb-16 pt-14 sm:pt-20 lg:grid-cols-2">
        <div>
          <p className="text-[13px] font-semibold uppercase tracking-wider text-brand-600">{c.eyebrow}</p>
          <h1 className="mt-3 text-4xl font-semibold leading-tight tracking-tight sm:text-5xl">{c.title}</h1>
          <p className="mt-5 text-lg leading-relaxed text-slate-600">{c.subtitle}</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button size="xl" asChild>
              <Link to="/signup">
                Start Free <ArrowRight />
              </Link>
            </Button>
            <Button size="xl" variant="outline" asChild>
              <Link to="/review/demoabcfurniture2026review">See Demo</Link>
            </Button>
          </div>
        </div>
        <ProductDemo />
      </section>
      <section className="border-y border-line bg-white">
        <div className="mx-auto grid max-w-6xl gap-6 px-5 py-20 md:grid-cols-3">
          {c.blocks.map((b) => (
            <div key={b.title}>
              <h2 className="text-lg font-semibold">{b.title}</h2>
              <p className="mt-2 text-[15px] leading-relaxed text-slate-600">{b.text}</p>
            </div>
          ))}
        </div>
      </section>
      <section className="mx-auto max-w-6xl px-5 py-20">
        <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr]">
          <h2 className="text-3xl font-semibold tracking-tight">What you get</h2>
          <ul className="grid gap-3 sm:grid-cols-2">
            {c.checklist.map((p) => (
              <li key={p} className="flex gap-2.5 rounded-xl border border-line bg-white p-4 text-[14.5px] text-slate-700">
                <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-green-600" /> {p}
              </li>
            ))}
          </ul>
        </div>
        {c.quote && (
          <figure className="mx-auto mt-16 max-w-3xl text-center">
            <blockquote className="text-2xl font-medium leading-snug tracking-tight text-ink">“{c.quote.text}”</blockquote>
            <figcaption className="mt-4 text-sm text-slate-500">{c.quote.who}</figcaption>
          </figure>
        )}
      </section>
      <FinalCta />
    </>
  );
}

export const LANDINGS: Record<string, LandingContent> = {
  "website-feedback": {
    eyebrow: "Website feedback",
    title: "Feedback pinned to the exact pixel",
    subtitle: "Clients click any element on your live or staging site and leave a comment right there. You get the screenshot, URL, element, device and browser — automatically.",
    blocks: [
      { title: "Smart element targeting", text: "We store a robust CSS selector, XPath, element text and click offset — so markers re-attach even after you change the layout. If an element is gone, we tell you." },
      { title: "Review link or script", text: "Share a secure link that loads the site in a review environment, or paste one async script tag for login-protected pages and strict CSP sites." },
      { title: "Versions", text: "Start a new version after each round of fixes. Feedback, resolution counts and approvals are tracked per version." },
    ],
    checklist: ["Click-to-comment on desktop and mobile (tap)", "Hover highlight before clicking", "Automatic screenshot with click point", "URL, page title, viewport, device, OS, browser", "Attachments: images, PDFs, docs", "Markers survive layout changes"],
  },
  "client-feedback": {
    eyebrow: "Client reviews & approvals",
    title: "Clients review without signing up",
    subtitle: "Send one link. Your client enters their name, clicks, comments, and approves the page — from their laptop or phone.",
    blocks: [
      { title: "No-login review links", text: "Unguessable links you can expire or revoke anytime. Clients only need a name and email." },
      { title: "Threads clients understand", text: "Replies show up right on the page. Clients can resolve or reopen items themselves." },
      { title: "Approvals on record", text: "Approve or request changes — saved with person, version, message and timestamp, visible on your dashboard." },
    ],
    checklist: ["Approve Page & Request Changes", "All / My feedback filters", "Email updates when feedback is fixed", "One-tap WhatsApp share", "Brand colour on the review toolbar", "Client-role accounts for repeat clients"],
  },
  "mockup-feedback": {
    eyebrow: "Mockup feedback",
    title: "Get design sign-off before you build",
    subtitle: "Upload PNG, JPG, WebP or PDF designs. Clients click anywhere on the design to comment — every pin becomes a task.",
    blocks: [
      { title: "Images & PDFs", text: "Homepage comps, brochures, social creatives or multi-page PDFs." },
      { title: "Same simple flow", text: "The same review link, markers, threads and approvals your clients already know." },
      { title: "Into the same board", text: "Design feedback lands in the same Kanban board as website feedback." },
    ],
    checklist: ["PNG, JPG, JPEG, WebP, PDF", "Pin comments anywhere", "Threaded replies", "Approval per design", "Version uploads", "Rolling out in the next release"],
  },
  "task-management": {
    eyebrow: "Task management",
    title: "Every comment becomes a task",
    subtitle: "No copy-pasting into Trello. Feedback arrives as a task with priority, assignee, screenshot and a full conversation.",
    blocks: [
      { title: "Kanban board", text: "Open, In Progress, Awaiting Review and Resolved. Drag and drop on desktop or touch." },
      { title: "Assign & prioritise", text: "Low, Medium, High, Critical. Assign one or more teammates and see everyone's workload." },
      { title: "Activity log", text: "Who changed what and when — status, priority, assignments, replies and internal notes." },
    ],
    checklist: ["Drag-and-drop Kanban", "Multiple assignees", "Due dates & overdue alerts", "Internal notes hidden from clients", "Attachments with upload progress", "Analytics: resolution time & workload"],
  },
  agencies: {
    eyebrow: "For agencies",
    title: "Every client, every website, one calm dashboard",
    subtitle: "Replace the WhatsApp groups, email threads and spreadsheets. Give each client a link, give each developer a board.",
    blocks: [
      { title: "Client isolation", text: "Clients only ever see their own projects. Your other clients' data stays private — enforced on the server." },
      { title: "Team workflow", text: "Assign feedback to developers and designers, track workload and resolution time across the agency." },
      { title: "Look professional", text: "Branded review toolbar and WhatsApp/email templates with your agency name." },
    ],
    checklist: ["20 websites on Professional", "Owner, Admin, Member & Client roles", "Approvals per website version", "WhatsApp review requests", "GST invoices", "Audit log of every change"],
  },
  freelancers: {
    eyebrow: "For freelancers",
    title: "Professional client reviews for ₹499 a month",
    subtitle: "Send a review link instead of screenshots. Get every change request in writing and the final approval on record.",
    blocks: [
      { title: "Stop scope creep", text: "Every request is documented with a timestamp. Easy to point at when “one small change” becomes ten." },
      { title: "Faster sign-off", text: "Clients approve the page in one click — then send the final invoice with confidence." },
      { title: "Works on any stack", text: "WordPress, Shopify, Webflow exports, React — if it has a URL, you can review it." },
    ],
    checklist: ["Free Starter plan", "20 websites on Professional", "Mobile-friendly for clients", "Approvals before final payment", "Pay by UPI", "Setup in two minutes"],
  },
  startups: {
    eyebrow: "For startups & product teams",
    title: "Stakeholder feedback and QA on staging, minus the chaos",
    subtitle: "Founders, marketing, QA and design comment on the same staging build. Every bug arrives with browser, OS and viewport attached.",
    blocks: [
      { title: "Works with SPAs", text: "React and Next.js apps keep routing normally in review mode, or install the widget in your staging build." },
      { title: "Release versions", text: "Track feedback per release, see what's resolved and get a sign-off before you ship." },
      { title: "Clean bug reports", text: "DOM selector, element text and screenshot help developers reproduce issues in seconds." },
    ],
    checklist: ["Unlimited reviewers", "Priority & due dates", "Kanban with drag and drop", "Async, non-blocking widget", "Analytics on resolution time", "Role-based permissions"],
  },
};
